import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import {
    getFullMediaScale,
    getFullMediaTranslationRange,
} from 'js/src/agent/video-state/serialization/mediaHelpers';

/**
 * The reframe context an off-frame finding needs to be acted on in one move. The position range
 * is per asset and grows with the media scale, so the copy the agent read from the video state
 * before its last edit is stale by the time the review fires — and the only other way to learn
 * the limit is to exceed it. Best-effort: never fails the review over it.
 */
export function describeMediaFraming(scene: Scene): string {
    try {
        const framings = scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
            .filter(
                (asset) =>
                    VideoData.isMediaAsset(asset) && asset.isEditable && asset.isSourceDataSet,
            )
            .map((asset) => {
                const { mediaXTranslationRange, mediaYTranslationRange } =
                    getFullMediaTranslationRange(asset);
                // The range helper keeps the sign of (media - viewport); the limit the setter
                // enforces is symmetric, so report the magnitude.
                const xLimit = Math.abs(mediaXTranslationRange[1]);
                const yLimit = Math.abs(mediaYTranslationRange[1]);
                return `${asset.name} is at ${getFullMediaScale(asset)} scale, so position accepts x within ±${xLimit}px and y within ±${yLimit}px`;
            });
        if (framings.length === 0) {
            return '';
        }
        return ` If the media is what is cut off: ${framings.join('; ')}. Raise the scale to widen the range.`;
    } catch {
        return '';
    }
}
